/**
 * Expert scores component: desktop grid, mobile pills and badge overlay.
 *
 * - Handles the responsive layout (desktop/mobile)
 * - Moves the mobile pills to the expert-target block
 * - Uses 'ref' attributes to find its elements
 *
 * @extends HTMLElement
 */


class ExpertScoresComponent extends HTMLElement {
  constructor() {
    super();
    this.elements = {};
    this.isMobile = false;
    this.resizeTimeout = null;
    this.expertTarget = null;
  }

  /**
   * Component setup
   */
  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    
    // Wait for the DOM to be ready before initialising
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeWithRetry();
      });
    } else {
      this.initializeWithRetry();
    }
  }

  /**
   * Setup with retries until productImage exists
   */
  initializeWithRetry(attempt = 0) {
    // Retry finding productImage if it was not found on the first pass
    if (!this.productImage) {
      this.productImage = document.querySelector('.product-information__media');
    }
    
    if (!this.expertScoresSection) {
      this.expertScoresSection = document.querySelector('.expert-scores-section');
    }
    
    // Not found yet and fewer than 5 attempts: retry
    if (!this.productImage && attempt < 5) {
      setTimeout(() => this.initializeWithRetry(attempt + 1), 100);
      return;
    }
    
    // Force the initial layout (first load)
    this.isMobile = window.innerWidth <= 749;
    this.updateLayout();
  }

  /**
   * DOM element lookup
   */
  initializeElements() {
    this.elements = {
      desktopGrid: this.querySelector('[ref="desktopGrid"]'),
      mobilePills: this.querySelector('[ref="mobilePills"]'),
      expertTitle: this.querySelector('[ref="expertTitle"]')
    };

    // Find the expert-target element
    const expertTargetBlock = document.querySelector('[data-expert-target-block]');
    if (expertTargetBlock) {
      this.expertTarget = expertTargetBlock.querySelector('[data-expert-mobile-target]');
    }

    // Find the product media container for the badge
    this.productImage = document.querySelector('.product-information__media');

    // Find the expert scores section for scrolling
    this.expertScoresSection = document.querySelector('.expert-scores-section');

    // Show the help message if needed
    this.showHelpIfNeeded();
  }

  /**
   * Event listeners
   */
  setupEventListeners() {
    window.addEventListener('resize', this.debounce(() => {
      this.handleResponsiveLayout();
    }, 250));
    this.setupAboutCardClicks();
  }

  /**
   * Click on an expert card: updates the About block (name, score, text)
   */
  setupAboutCardClicks() {
    const section = this.closest('.expert-scores-section') || document.querySelector('.expert-scores-section');
    if (!section) return;
    const cards = section.querySelectorAll('.expert-scores__card--clickable');
    const aboutBlock = section.querySelector('[data-expert-about-block]');
    const aboutName = section.querySelector('[data-expert-about-name]');
    const aboutScore = section.querySelector('[data-expert-about-score]');
    const aboutText = section.querySelector('[data-expert-about-text]');
    if (!aboutName || !aboutScore || !aboutText) return;
    const aboutLabel = (aboutBlock && aboutBlock.getAttribute('data-about-label')) || 'About';
    const scoreLabel = (aboutBlock && aboutBlock.getAttribute('data-score-label')) || 'Score';
    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const name = card.getAttribute('data-expert-name') || '';
        const score = card.getAttribute('data-expert-score') || '';
        const scale = card.getAttribute('data-expert-scale') || '';
        const about = card.getAttribute('data-expert-about') || '';
        aboutName.textContent = aboutLabel + ' # ' + name;
        aboutScore.textContent = scoreLabel + ': ' + score + (scale ? ' / ' + scale : '');
        aboutText.textContent = about;
        cards.forEach((c) => c.removeAttribute('aria-pressed'));
        card.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /**
   * Responsive layout handling
   */
  handleResponsiveLayout() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 749;

    if (wasMobile !== this.isMobile) {
      this.updateLayout();
    }
  }

  /**
   * Layout update for the current screen size
   */
  updateLayout() {
    if (this.isMobile) {
      this.showMobileLayout();
    } else {
      this.showDesktopLayout();
    }
  }

  /**
   * Mobile layout: pills on top (target) + section with cards + about below
   */
  showMobileLayout() {
    // Show the grid (cards + about) at the bottom of the page
    if (this.elements.desktopGrid) {
      this.elements.desktopGrid.style.display = '';
    }

    if (this.elements.expertTitle) {
      this.elements.expertTitle.style.display = 'block';
    }

    // Move the pills to the target (under the product image) when 2+ critics
    this.movePillsToTarget();
  }

  /**
   * Desktop layout
   */
  showDesktopLayout() {
    // Show the desktop grid (flex for the row variant + about)
    if (this.elements.desktopGrid) {
      this.elements.desktopGrid.style.display = '';
    }

    // Show the title
    if (this.elements.expertTitle) {
      this.elements.expertTitle.style.display = 'block';
    }

    // Put the mobile pills back in the section
    this.movePillsToSection();

    // The badge is rendered server-side
  }

  /**
   * Move the mobile pills to expert-target
   */
  movePillsToTarget() {
    if (!this.elements.mobilePills) {
      return;
    }

    if (!this.expertTarget) {
      return;
    }

    if (this.elements.mobilePills.parentNode === this.expertTarget) {
      return;
    }

    const existingPills = this.expertTarget.querySelector('.expert-scores__mobile-pills-container');
    if (existingPills) {
      return;
    }

    this.expertTarget.style.display = 'block';
    this.expertTarget.parentElement.style.display = 'block';
    
    this.expertTarget.appendChild(this.elements.mobilePills);
  }

  /**
   * Put the mobile pills back in the section
   */
  movePillsToSection() {
    if (!this.elements.mobilePills) {
      return;
    }

    if (this.elements.mobilePills.parentNode === this) {
      return;
    }

    if (this.elements.desktopGrid) {
      this.elements.desktopGrid.insertAdjacentElement('afterend', this.elements.mobilePills);
    } else {
      this.insertBefore(this.elements.mobilePills, this.firstChild);
    }
    
    if (this.expertTarget) {
      this.expertTarget.style.display = 'none';
      this.expertTarget.parentElement.style.display = 'none';
    }
  }

  /**
   * Show the help message if needed
   */
  showHelpIfNeeded() {
    const helpElement = this.querySelector('[data-expert-help]');
    if (helpElement && !this.expertTarget) {
      helpElement.style.display = 'block';
    } else if (helpElement) {
      helpElement.style.display = 'none';
    }
  }


  /**
   * Debounce helper
   */
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.define('expert-scores-component', ExpertScoresComponent);

/**
 * Smooth scroll for the expert badges (server-side rendered). Runs outside the custom
 * element because the badges live in .product-information__media, not in
 * <expert-scores-component>.
 */
function setupBadgeSmoothScroll() {
  const badgeLinks = document.querySelectorAll('.expert-badge__link');
  
  if (badgeLinks.length === 0) {
    return;
  }
  
  badgeLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Target selector from data-scroll-target
      const targetSelector = link.getAttribute('data-scroll-target');
      const targetSection = document.querySelector(targetSelector);
      
      if (targetSection) {
        // Section position with an offset for the header
        const headerOffset = 80; // adjust to the sticky header height
        const elementPosition = targetSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        // window.scrollTo for a controlled smooth scroll
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });
  });
}

// Run now if the DOM is already loaded (deferred script), otherwise wait for DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupBadgeSmoothScroll);
} else {
  // DOM already loaded (deferred script), run now
  setupBadgeSmoothScroll();
}
